import {
  ConfigStateService,
  DynamicLayoutComponent,
  PermissionService,
  ReplaceableComponentsService,
  RoutesService,
  TimezoneService,
} from '@abp/ng.core';
import { eIdentityRouteNames } from '@volo/abp.ng.identity/config';
import {
  InternetConnectionStatusComponent,
  LoaderBarComponent,
  NavItem,
  NavItemsService,
  UserMenu,
  UserMenuService,
} from '@abp/ng.theme.shared';
import { Component, inject } from '@angular/core';
import { GdprCookieConsentComponent } from '@volo/abp.ng.gdpr/config';
import { SettingTabsService } from '@abp/ng.setting-management/config';
import { IntegrationSettingsComponent } from './administration/settings/integration-settings/integration-settings.component';
import { eThemeLeptonXComponents } from '@volosoft/abp.ng.theme.lepton-x';
import { FooterComponent } from './shared/components/footer/footer.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from 'src/core/services/user/user.service';
import { AiDisclaimerModalComponent } from './shared/components/ai-disclaimer-modal/ai-disclaimer-modal.component';
import { OAuthService } from 'angular-oauth2-oidc';
import { DataImportComponent } from './administration/settings/data-import/data-import.component';
import { PhoneNumbersMainComponent } from './administration/settings/phone-numbers/components/phone-numbers-main/phone-numbers-main.component';
import { VersionModalComponent } from './shared/components/version-modal/version-modal.component';
import { Toast } from 'primeng/toast';
import { ConfirmDialogHostComponent } from 'src/core/services/confirmation/confirm-dialog-host/confirm-dialog-host.component';
import { UserSkillDialogComponent } from './features/agent-management/components/user-skill-dialog/user-skill-dialog.component';
import { AssignQueueDialogComponent } from './features/agent-management/components/assign-queue-dialog/assign-queue-dialog.component';
import { RejectCallDialogHostComponent } from './shared/agent-desktop/components/reject-call-dialog/reject-call-dialog-host.component';
import { AgentStatusToolbarComponent } from './shared/components/agent-status-toolbar/components/agent-status-toolbar/agent-status-toolbar.component';
import { QuickAvailableComponent } from './shared/components/agent-status-toolbar/components/quick-available/quick-available.component';
import { NotificationBellComponent } from './shared/components/notification-bell/notification-bell.component';
import { AgentStatusAccessService } from 'src/core/services/agent-status/agent-status-access.service';
import { filter, firstValueFrom } from 'rxjs';
import { WorkEnvironmentSettingsComponent } from './administration/settings/work-environment/work-environment-settings.component';
import { QueueManagementComponent } from './administration/settings/queues/queue-management.component';
import { LocalizationMainComponent } from './administration/settings/localization/localization-main.component';
import { InstanceManagementComponent } from './administration/settings/instance-management/instance-management.component';
import { SessionExitService } from 'src/core/services/user/session-exit.service';
import { eUserMenuItems } from '@volosoft/abp.ng.theme.lepton-x';
import { PermissionConstants } from 'src/core/constants/permission.constant';

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;

const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

/** Cached per zone: constructing an `Intl.DateTimeFormat` costs about a hundred times more than using one. */
function zoneFormatter(zone: string): Intl.DateTimeFormat {
  let formatter = zoneFormatters.get(zone);

  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    zoneFormatters.set(zone, formatter);
  }

  return formatter;
}

/** The wall clock `instant` reads in `zone`, expressed as a UTC timestamp (whole seconds). */
function tenantWallClockMillis(instant: Date, zone: string): number {
  const parts = zoneFormatter(zone).formatToParts(instant);
  const part = (type: string): number => Number(parts.find(p => p.type === type)?.value);

  return Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
    part('second'),
  );
}

/** Offset of `zone` at `instant`, in minutes east of UTC. */
function tenantOffsetInMinutes(instant: Date, zone: string): number {
  const wholeSeconds = Math.floor(instant.getTime() / MS_PER_SECOND) * MS_PER_SECOND;

  return Math.round((tenantWallClockMillis(instant, zone) - wholeSeconds) / MS_PER_MINUTE);
}

/** The instant whose wall clock in `zone` matches the local fields of `date`. */
function tenantWallClockToUtc(date: Date, zone: string): Date {
  const wallClock = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  );

  // A daylight-saving change can only fall between the day before and the day after, so these are the only
  // two instants the requested wall clock can map to.
  const offsetBefore = tenantOffsetInMinutes(new Date(wallClock - MS_PER_DAY), zone);
  const offsetAfter = tenantOffsetInMinutes(new Date(wallClock + MS_PER_DAY), zone);
  const withOffsetBefore = new Date(wallClock - offsetBefore * MS_PER_MINUTE);
  const withOffsetAfter = new Date(wallClock - offsetAfter * MS_PER_MINUTE);

  const requested = Math.floor(wallClock / MS_PER_SECOND) * MS_PER_SECOND;
  const beforeReadsBack = tenantWallClockMillis(withOffsetBefore, zone) === requested;
  const afterReadsBack = tenantWallClockMillis(withOffsetAfter, zone) === requested;

  if (beforeReadsBack !== afterReadsBack) {
    return beforeReadsBack ? withOffsetBefore : withOffsetAfter;
  }

  // Away from a transition both are the same instant. Otherwise the clock either ran the hour twice — the
  // earliest occurrence wins — or skipped it, where nothing reads back and going later keeps the value from
  // landing on the previous day.
  return new Date(
    beforeReadsBack
      ? Math.min(withOffsetBefore.getTime(), withOffsetAfter.getTime())
      : Math.max(withOffsetBefore.getTime(), withOffsetAfter.getTime()),
  );
}

@Component({
  selector: 'app-root',
  standalone: true,
  template: `
    <abp-loader-bar />
    <abp-dynamic-layout />
    <abp-gdpr-cookie-consent />
    <abp-internet-status />
    <p-toast position="bottom-right" [life]="4000" />
    <app-confirm-dialog-host />
    <app-user-skill-dialog />
    <app-assign-queue-dialog />
    <!--
      Same permission gate as the AgentStatusToolbar NavItem that starts the flow. Without it every
      user (login screen included) would bootstrap the CallFacade -> CallTabSync (opens a
      BroadcastChannel) -> AgentDesktopService chain at app start just to render nothing.
    -->
    @if (agentStatusAccess.isEnabled()) {
      <app-reject-call-dialog-host />
    }
  `,
  imports: [
    LoaderBarComponent,
    DynamicLayoutComponent,
    GdprCookieConsentComponent,
    InternetConnectionStatusComponent,
    Toast,
    ConfirmDialogHostComponent,
    UserSkillDialogComponent,
    AssignQueueDialogComponent,
    RejectCallDialogHostComponent,
  ],
})
export class AppComponent {
  private settingTabs = inject(SettingTabsService);
  private routesService = inject(RoutesService);
  private userMenuService = inject(UserMenuService);
  private navItemsService = inject(NavItemsService);
  protected agentStatusAccess = inject(AgentStatusAccessService);
  private replaceableComponentsService = inject(ReplaceableComponentsService);
  private modalService = inject(NgbModal);
  private userService = inject(UserService);
  private oAuthService = inject(OAuthService);
  private configStateService = inject(ConfigStateService);
  private sessionExit = inject(SessionExitService);
  private timezoneService = inject(TimezoneService);
  private modalShown = false;
  private agentStatusNavItemsRegistered = false;

  constructor() {
    this.overrideOutgoingDateSerialization();

    this.settingTabs.add([
      {
        name: 'Settings::IntegrationSettings',
        component: IntegrationSettingsComponent,
      },
      {
        name: 'Settings::DataImport:Title',
        component: DataImportComponent,
        requiredPolicy: PermissionConstants.canManageDataImport,
      },
      {
        name: 'Settings::PhoneNumbers:Title',
        component: PhoneNumbersMainComponent,
        requiredPolicy: PermissionConstants.canManageDidRouting,
      },
      {
        name: 'Settings::WorkEnvironment:Title',
        component: WorkEnvironmentSettingsComponent,
        requiredPolicy: PermissionConstants.canManageWorkEnvironmentSettings,
      },
      {
        name: 'Settings::Queues:TabTitle',
        component: QueueManagementComponent,
        requiredPolicy: PermissionConstants.agentManagement
      },
      {
        name: 'Settings::Localization:Title',
        component: LocalizationMainComponent,
        requiredPolicy: PermissionConstants.localizationSettingsTab,
      },
    ]);

    this.settingTabs.remove(['AbpSettingManagement::Menu:TimeZone']);

    // Removed from the product for both host and tenant users; view is kept, only the menu entry goes away.
    this.routesService.remove([eIdentityRouteNames.OrganizationUnits]);

    this.replaceableComponentsService.add({
      component: FooterComponent,
      key: eThemeLeptonXComponents.Footer,
    });

    const tenantId = this.configStateService.getDeep(['currentUser', 'tenantId']);
    const isHost = tenantId == null;
    if (isHost) {
      this.settingTabs.add([
        {
          name: 'FreeswitchIntegration::InstanceManagement',
          component: InstanceManagementComponent,
        },
      ]);

      // Removed from the product for the ABP host user; tenants keep these tabs.
      this.settingTabs.remove([
        'Settings::IntegrationSettings',
        'Settings::DataImport:Title',
        'Settings::PhoneNumbers:Title',
        'Settings::WorkEnvironment:Title',
        'Settings::Queues:TabTitle',
        'Settings::Localization:Title',
      ]);
    }
    void this.registerAgentStatusNavItems();
    this.registerNotificationNavItems();

    this.addUserMenuItems();
    this.patchLogoutAction();
    this.checkAndShowDisclaimer();
  }

  /**
   * Sends every outgoing `Date` as the instant its wall-clock fields represent in the tenant time zone
   * rather than the browser's, so a picked "08.03.2026 00:00" means the tenant's midnight everywhere.
   */
  private overrideOutgoingDateSerialization(): void {
    const timezoneService = this.timezoneService;

    Date.prototype.toJSON = function (this: Date): string {
      // Native toJSON returns null here; throwing would break the whole payload.
      if (!Number.isFinite(this.getTime())) {
        return null as unknown as string;
      }

      const zone = timezoneService.timezone;

      return zone
        ? tenantWallClockToUtc(this, zone).toISOString()
        : new Date(this.getTime()).toISOString();
    };
  }

  private addUserMenuItems(): void {
    this.userMenuService.addItems([
      new UserMenu({
        id: 'About',
        order: 105.5,
        action: () => this.openVersionModal(),
        textTemplate: { text: '::Menu:About', icon: 'fa fa-info-circle' },
      } as Partial<UserMenu>),
    ]);
  }

  private patchLogoutAction(): void {
    this.userMenuService.patchItem(eUserMenuItems.Logout, {
      action: () => {
        void this.sessionExit.logout();
      },
    });
  }

  private async registerAgentStatusNavItems(): Promise<void> {
    if (!this.agentStatusAccess.canUseStatusManagement() || this.agentStatusNavItemsRegistered) {
      return;
    }

    await firstValueFrom(
      this.configStateService.getAll$().pipe(filter(config => !!config?.currentUser)),
    );

    this.navItemsService.addItems([
      new NavItem({
        id: 'AgentStatusToolbar',
        component: AgentStatusToolbarComponent,
        order: 89,
      }),
      new NavItem({
        id: 'QuickAvailable',
        component: QuickAvailableComponent,
        order: 91,
      }),
    ]);

    this.agentStatusNavItemsRegistered = true;
  }

  private registerNotificationNavItems(): void {
    this.navItemsService.addItems([
      new NavItem({
        id: 'NotificationBell',
        component: NotificationBellComponent,
        order: 88,
      }),
    ]);
  }

  private openVersionModal(): void {
    this.modalService.open(VersionModalComponent, {
      centered: true,
      backdrop: true,
      keyboard: true,
    });
  }

  private checkAndShowDisclaimer(): void {
    if (!this.oAuthService.hasValidAccessToken() || this.modalShown) {
      return;
    }

    this.userService.getConfirmedAiUsageDisclaimer().subscribe({
      next: confirmed => {
        if (!confirmed && !this.modalShown) {
          this.showAiDisclaimerModal();
        }
      },
      error: error => {
        console.error('AI Disclaimer check failed:', error);
      },
    });
  }

  private showAiDisclaimerModal(): void {
    this.modalShown = true;

    const modalRef = this.modalService.open(AiDisclaimerModalComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'ai-disclaimer-modal',
      centered: true,
    });

    modalRef.result.finally(() => {
      this.modalShown = false;
    });
  }
}
